const META_PIXEL_SCRIPT_SRC = 'https://connect.facebook.net/en_US/fbevents.js';

type FbqStub = {
    (...args: unknown[]): void;
    callMethod?: (...args: unknown[]) => void;
    queue?: unknown[][];
    loaded?: boolean;
    version?: string;
};

let hasInitializedMetaPixel = false;
let hasRequestedMetaPixelScript = false;
let lastTrackedPageKey = '';

const isBrowser = (): boolean => typeof window !== 'undefined';

const isMetaPixelAllowed = (): boolean => {
    // This indirection keeps initialization ready for future consent gates.
    return true;
};

const resolveMetaPixelId = (): string => {
    const pixelId = process.env.REACT_APP_META_PIXEL_ID
        || process.env.REACT_APP_FACEBOOK_PIXEL_ID
        || process.env.REACT_APP_FB_PIXEL_ID
        || '';

    return pixelId.trim();
};

const warnDevOnly = (message: string, error?: unknown): void => {
    if (process.env.NODE_ENV !== 'development') {
        return;
    }

    if (error) {
        console.warn(`[meta-pixel] ${message}`, error);
        return;
    }

    console.warn(`[meta-pixel] ${message}`);
};

const ensureFbqStub = (): void => {
    if (!isBrowser() || window.fbq) {
        return;
    }

    const fbq: FbqStub = (...args: unknown[]) => {
        if (fbq.callMethod) {
            fbq.callMethod(...args);
            return;
        }

        fbq.queue = fbq.queue || [];
        fbq.queue.push(args);
    };

    fbq.queue = [];
    fbq.loaded = true;
    fbq.version = '2.0';

    window.fbq = fbq;
    if (!window._fbq) {
        window._fbq = fbq;
    }
};

const loadMetaPixelScript = (): void => {
    if (!isBrowser() || hasRequestedMetaPixelScript) {
        return;
    }

    const existingScript = document.querySelector<HTMLScriptElement>(`script[src="${META_PIXEL_SCRIPT_SRC}"]`);
    if (existingScript) {
        hasRequestedMetaPixelScript = true;
        return;
    }

    const script = document.createElement('script');
    script.async = true;
    script.defer = true;
    script.src = META_PIXEL_SCRIPT_SRC;
    script.onerror = (error) => {
        warnDevOnly('Failed to load Meta Pixel script.', error);
    };

    document.head.appendChild(script);
    hasRequestedMetaPixelScript = true;
};

/**
 * Initializes Meta Pixel once per app lifecycle.
 */
export const initMetaPixel = (): void => {
    if (!isBrowser() || hasInitializedMetaPixel || !isMetaPixelAllowed()) {
        return;
    }

    const pixelId = resolveMetaPixelId();
    if (!pixelId) {
        warnDevOnly('REACT_APP_META_PIXEL_ID is missing. Pixel initialization skipped.');
        return;
    }

    ensureFbqStub();
    loadMetaPixelScript();

    if (!window.fbq) {
        warnDevOnly('Meta Pixel is not available on window.');
        return;
    }

    try {
        window.fbq('init', pixelId);
        hasInitializedMetaPixel = true;
    } catch (error) {
        warnDevOnly('Meta Pixel init call failed.', error);
    }
};

/**
 * Tracks a standard Meta Pixel PageView event.
 */
export const trackPageView = (pageKey?: string): void => {
    const resolvedPageKey = pageKey || (isBrowser()
        ? `${window.location.pathname}${window.location.search}${window.location.hash}`
        : '');

    if (resolvedPageKey && resolvedPageKey === lastTrackedPageKey) {
        return;
    }

    trackEvent('PageView');

    if (resolvedPageKey) {
        lastTrackedPageKey = resolvedPageKey;
    }
};

/**
 * Tracks a Meta Pixel event with optional payload parameters.
 *
 * Ecommerce examples:
 * trackEvent('ViewContent', {
 *   content_ids: [product.id],
 *   content_name: product.name,
 *   value: product.price,
 *   currency: 'BDT'
 * });
 *
 * trackEvent('AddToCart', {
 *   content_ids: [product.id],
 *   value: product.price,
 *   currency: 'BDT'
 * });
 *
 * trackEvent('InitiateCheckout', {
 *   value: cart.total,
 *   currency: 'BDT'
 * });
 *
 * trackEvent('Purchase', {
 *   value: order.total,
 *   currency: 'BDT',
 *   contents: order.items
 * });
 */
export const trackEvent = (eventName: string, params?: Record<string, any>): void => {
    if (!isBrowser()) {
        return;
    }

    // Guards against effect ordering where tracker fires before app-level init effect.
    if (!hasInitializedMetaPixel) {
        initMetaPixel();
    }

    if (!window.fbq) {
        warnDevOnly(`Skipped Meta Pixel event "${eventName}" because fbq is unavailable.`);
        return;
    }

    try {
        if (params && Object.keys(params).length > 0) {
            window.fbq('track', eventName, params);
            return;
        }

        window.fbq('track', eventName);
    } catch (error) {
        warnDevOnly(`Meta Pixel event "${eventName}" failed.`, error);
    }
};
